export const enGrmsTranslations = {
    // The seven derived display states (§3.1) plus the labels that are not part
    // of the derived state and count independently.
    status: {
        total: 'Total', occupied: 'Occupied', ready: 'Ready',
        dirty: 'Dirty', cleaning: 'Cleaning', to_inspect: 'To Inspect',
        out_of_order: 'Out of Order', out_of_service: 'Out of Service',
        // The card frame and floor-plan cell collapse the three housekeeping
        // details into one reading; the icon chip carries the detail (§5.6).
        needs_housekeeping: 'Needs Housekeeping',
        unavailable: 'Unavailable', notOccupied: 'Not occupied',
        // Raw occupancy/service axis values (`OccupancyStatus`/`ServiceState`) that
        // are not themselves derived display states, but appear verbatim in a room
        // event's `previousValue`/`newValue` (LogTab's status-change description).
        vacant: 'Vacant', in_service: 'In Service',
    },
    // Compact card badges and floor-plan cells. Full `status` labels remain for
    // filters, drawer headers, titles, and accessible names.
    statusShort: {
        occupied: 'Occupied', ready: 'Ready', dirty: 'Dirty', cleaning: 'Cleaning',
        to_inspect: 'To inspect', out_of_order: 'Out of order', out_of_service: 'Out of service',
        needs_housekeeping: 'Housekeeping',
    },
    common: {
        room: 'Room', guest: 'Guest', all: 'All', status: 'Status', access: 'Access',
        alarms: 'Alarms', log: 'Log', graphs: 'Graphs', retry: 'Retry', cancel: 'Cancel',
        done: 'Done', on: 'On', off: 'Off', open: 'Open', closed: 'Closed', unknown: 'unknown',
    },
    dashboard: {
        search: 'Search...', searchRoomsGuests: 'Search rooms, guests...', filters: 'Filters',
        clear: 'Clear', dashboardView: 'Dashboard view', showRoomGrid: 'Show room grid',
        roomGrid: 'Room grid', showFloorPlans: 'Show floor plans', floorPlans: 'Floor plans',
        gridView: 'Grid view', compactView: 'Compact view', unassigned: 'Unassigned',
        noRooms: 'No rooms yet', noRoomsDescription: 'Rooms will appear here once they are configured in the system.',
        noMatches: 'No matching rooms', noMatchesDescription: 'Try adjusting your filters.',
        clearFilters: 'Clear filters', roomAria: 'Room {{room}}, {{status}}', windowOpen: 'Window open',
        doorOpen: 'Door open', doorClosed: 'Door closed', modeUnknown: 'Mode unknown',
        lastReported: 'Last reported', lastReadingGatewayOffline: 'Not responding · last reading {{time}}',
        gatewayOffline: 'Not responding', gatewayNotReporting: '{{gateway}} not reporting', lockLocked: 'Lock locked',
        lockUnlocked: 'At least one lock reported unlocked', lockUnknown: 'Lock state unknown',
        legendColor: 'Colour',
        floorOrderUnavailable: 'Floor order unavailable — showing floors in an unconfirmed order.',
    },
    filters: {
        alarmsOnly: 'Alarms only', floor: 'Floor', allFloors: 'All floors', type: 'Type',
        dnd: 'Do not disturb', mur: 'Make up room',
    },
    alarms: {
        critical_one: 'Critical Alarm', critical_other: '{{count}} Critical Alarms',
        fallback: 'Critical alarm', more: '(+{{count}} more)', dismiss: 'Dismiss alarm banner',
        criticalLabel: 'Critical', warnings: 'Warnings', warning: 'Warning', alarms: 'Alarms', info: 'Info',
        mute: 'Mute alarm audio', unmute: 'Unmute alarm audio',
        acknowledge: 'Acknowledge', acknowledged: 'Acknowledged', resolve: 'Resolve',
        createdAt: 'Created', acknowledgedAt: 'Acknowledged', sos: 'SOS',
        motionInVacant: 'Motion detected in vacant room',
        hvacSnoozed: 'Acknowledged by {{who}} · checks again {{time}}',
        inRoom: '{{alarm}} in room {{room}}',
    },
    // Short flag words on cards and rows (UX-30): never the bare literal in Croatian.
    flags: { dnd: 'DND', mur: 'MUR' },
    floorPlan: {
        positioned: '{{count}} positioned', openRoom: 'Open room {{room}}, {{status}}',
        noMatchingPlacements: 'No matching rooms have usable placements in this plan.',
        unplacedSection: 'Rooms without floor-plan placement',
        unplacedTitle: 'Rooms without a usable placement',
        unplacedDescription: 'These rooms remain available in the grid.',
    },
    desk: {
        today: 'Today', checkInGuest: 'Check in guest',
        tile: {
            arrivals: 'Arrivals', departures: 'Departures', sellable: 'Ready to sell', attention: 'Needs attention',
        },
        tileSub: {
            arrivals: 'checked in today', departures: 'to go · due {{time}}',
            departuresOut: '{{count}} out ·', departuresOverdue: 'to go · {{count}} overdue',
        },
        showOnBoard: 'Show on the board: {{tile}}', clearTile: 'Stop showing only {{tile}}',
        attention: 'Attention', openActivity: 'Open activity',
        countAria_one: '{{count}} item needs attention', countAria_other: '{{count}} items need attention',
        action: {
            acknowledge: 'Acknowledge', openStay: 'Open stay', openRoom: 'Open room', openClimate: 'Open climate',
            notifyHousekeeping: 'Notify housekeeping', details: 'Details', hideDetails: 'Hide details',
            open: 'Open', notify: 'Notify', openKeys: 'Open keys', checkOut: 'Check out',
        },
        row: {
            overdue: 'Overdue checkout', overdueDetail: '{{guest}} · due {{time}}', departure: 'Leaving today',
            keysActive_one: '{{count}} key active', keysActive_other: '{{count}} keys active',
            keys_one: '{{count}} key', keys_other: '{{count}} keys',
            doorLeftOpen: 'Door left open', doorVacant: 'Vacant room', doorDnd: 'Do not disturb',
            mur: 'Make up room requested', gateway_one: 'Room {{rooms}} not responding', gateway_other: 'Rooms {{rooms}} not responding',
            gatewayShown: 'last readings shown', gatewaySince: 'since {{time}}', gatewayTitle: 'Gateway {{gateway}}',
            critical: 'Critical alarm', warning: 'Warning', acknowledgedBy: 'acknowledged by {{user}}',
            inHouse: '{{guest}} in house', guest: 'Guest',
            keysExpiring_one: '{{count}} key expires today', keysExpiring_other: '{{count}} keys expire today',
            keysUntil: 'valid until {{time}}', longDirty: 'Dirty for over a day',
        },
        nightAudit: {
            title: 'Night audit', toggle: 'Night audit',
            hint: 'Show only overdue checkouts, keys expiring today and rooms dirty for over a day',
            nothing: 'Nothing to close out: no overdue checkouts, no keys expiring today, no room dirty for over a day.',
        },
        alarm: {
            sos: 'SOS pressed', lock_forced: 'Door forced', tamper: 'Tamper alarm', burglary: 'Burglary alarm',
            hvac_deviation: 'Temperature off target', probe_error: 'Temperature probe fault',
            motion_in_vacant: 'Motion in a vacant room', overdue_checkout: 'Overdue checkout',
        },
        age: {
            seconds: '{{count}} s ago', minutes: '{{count}} min', hours: '{{hours}} h {{minutes}}',
            over: '{{age}} over',
        },
        pane: { label: 'Desk view', desk: 'Desk', board: 'Board', activity: 'Activity' },
        ready: 'Ready', notReady: 'Not ready', noSensors: 'No sensors',
        notReadyWhy: {
            commissioning: 'Not ready · commissioning', maintenance: 'Not ready · maintenance', inactive: 'Not ready · inactive',
            alarm: 'Not ready · alarm open',
        },
        toolbar: {
            rooms: 'Rooms', floorPlan: 'Floor plan', compact: 'Compact', alarmSound: 'Alarm sound', more: 'More board options',
        },
    },
    drawer: {
        details: 'Room {{room}} Details', description: 'Stay, climate, door and keys, log',
        hvac: 'HVAC', access: 'Access', log: 'Log', graphs: 'Graphs',
        stayTab: 'Stay', climateTab: 'Climate', doorKeysTab: 'Door & keys', more: 'More',
        checkInAt: 'Check-in: {{date}}', expectedCheckout: 'Expected checkout: {{date}}',
        checkIn: 'Check in', editStay: 'Edit stay', checkOut: 'Check out',
        markOutOfOrder: 'Mark out of order', markOutOfService: 'Mark out of service',
        startCleaning: 'Start cleaning', markDone: 'Mark done',
        markInspected: 'Mark inspected', markForService: 'Mark for service', clearMaintenance: 'Clear maintenance',
        restoreToService: 'Restore to service',
        presence: 'In room', noPresence: 'No presence', lightsOn: '{{count}} on', noDeviceData: 'No device data',
    },
    // Toast text for every room-dialog action: `done` confirms, `failed` is the title above the
    // server's own reason (UX-26).
    feedback: {
        checkOut: { done: 'Guest checked out', failed: 'Check-out failed' },
        housekeeping: { done: 'Housekeeping status updated', failed: 'Housekeeping update failed' },
        serviceState: { done: 'Service state updated', failed: 'Service state change failed' },
        alarmAcknowledge: { done: 'Alarm acknowledged', failed: 'Could not acknowledge the alarm' },
        alarmResolve: { done: 'Alarm resolved', failed: 'Could not resolve the alarm' },
        murAcknowledge: { done: 'Make Up Room acknowledged', failed: 'Could not acknowledge Make Up Room' },
        cardRevoke: { done: 'Card revoked', failed: 'Could not revoke the card' },
        cardReportLost: { done: 'Key reported lost and revoked', failed: 'Could not report the key lost' },
        note: { done: 'Note saved', failed: 'Note was not saved' },
        cardsRevokeAll: { done: 'All room cards revoked', failed: 'Could not revoke the room cards' },
        lockLock: { done: 'Lock command sent', failed: 'Could not lock the door' },
        lockUnlock: { done: 'Unlock command sent', failed: 'Could not unlock the door' },
        lockOpen: { done: 'Open command sent', failed: 'Could not open the door' },
        climate: { done: 'Climate setting saved', failed: 'Climate setting was not saved' },
        scene: { done: 'Scene activated', failed: 'Could not activate the scene' },
    },
    access: {
        opened: 'Opened', closed: 'Closed', noLocksCards: 'No locks or cards configured for this room',
        insufficientPermissions: 'Insufficient permissions',
        readerNotReady: 'Card reader not ready', guestCards: 'Guest Cards ({{count}})', issueCard: 'Issue Card',
        revokeAll: 'Revoke All', placeCard: 'Reader ready · place a blank card to issue', cardReady: 'Card ready: {{uid}}',
        guest: 'Guest', validUntil: 'until {{date}}', revokeCard: 'Revoke card', replaceCard: 'Replace card',
        critical: 'Critical', lock: 'Lock', unlock: 'Unlock', open: 'Open', active: 'Active', pending: 'Pending',
        suspended: 'Suspended', revoked: 'Revoked', expired: 'Expired', lost: 'Lost',
        lastAccess: 'Last access',
        lockbell: 'Lockbell', minibar: 'Minibar',
        cardSuffix: 'Card ****{{card}}',
    },
    hvac: {
        none: 'No HVAC zones or temperature sensors configured', ecoMode: 'Eco Mode', quickScenes: 'Quick Scenes',
        lowerSetpoint: 'Lower setpoint', raiseSetpoint: 'Raise setpoint',
        modeSetForBuilding: 'Set for the building', windowDisabled: 'Window open — HVAC disabled',
        mode: 'Mode', fan: 'Fan', actual: 'Actual', vacantRoomWarning: 'This room is vacant; occupancy automation may replace these settings at check-in.',
        option: { auto: 'Auto', off: 'Off', cool: 'Cooling', heat: 'Heating', fan_only: 'Fan', dry: 'Dry', low: 'Low', med: 'Med', medium: 'Medium', high: 'High', turbo: 'Turbo' },
        setpointOutOfRange: 'The room reports {{value}}°C, outside its configured {{min}}–{{max}}°C range. Adjust it on the device or widen the range in the room parameters.',
        // Standby and Protection replace the mode while the thermostat holds a setback (H5).
        special: { standby: 'Standby', protection: 'Protection' },
        setback: { standby: 'Setback while the room is unoccupied', protection: 'Frost and overheat protection' },
        // Hotel-relative thermostat (BMS-spec hotel-relative-thermostat.md). The card shows real degrees only;
        // the base is named where a template parameter is edited, never on the climate card.
        hotel: {
            base: 'Hotel base',
            pending: 'Applying…', pendingValue: 'Pending {{value}}°C',
            rejected: 'The device rejected the last change', conflict: 'The device holds a different policy',
            retry: 'Resend',
            acknowledge: 'Use device values',
            locked: 'Guest controls are locked for this zone',
        },
    },
    log: {
        all: 'All', access: 'Access', status: 'Status', housekeeping: 'Housekeeping', maintenance: 'Maintenance',
        hvac: 'Climate', scenes: 'Scenes', alarms: 'Alarms',
        manualOnly: 'Manual only', allAccess: 'All Access', today: 'Today', yesterday: 'Yesterday',
        showDeviceEvents: 'Show device events',
        none: 'No events recorded', loadMore: 'Load more', inspectionNote: 'Inspection note: {{note}}',
        unknownReason: 'Unknown reason', accessGranted: 'Access granted', accessGrantedActor: 'Access granted — {{actor}}',
        accessGrantedCard: 'Access granted via card ...{{card}}', accessGrantedActorCard: 'Access granted — {{actor}} (Card ending in ...{{card}})',
        accessDenied: 'Access denied — {{reason}}', manualUnlock: 'Manual unlock', manualUnlockActor: 'Manual unlock by {{actor}}',
        manualLock: 'Manual lock', manualLockActor: 'Manual lock by {{actor}}', emergencyUnlock: 'Emergency unlock',
        emergencyUnlockActor: 'Emergency unlock by {{actor}}', checkIn: 'Check-in — {{guest}}', checkOut: 'Check-out — {{guest}}',
        dndOn: 'Do Not Disturb enabled', dndOff: 'Do Not Disturb disabled', murOn: 'Make Up Room enabled', murOff: 'Make Up Room disabled',
        cleaningStarted: 'Cleaning started', cleaningStartedActor: 'Cleaning started by {{actor}}',
        cleaningDone: 'Cleaning completed', cleaningDoneActor: 'Cleaning completed by {{actor}}',
        inspected: 'Room inspected', inspectedActor: 'Room inspected by {{actor}}',
        maintenanceReported: 'Maintenance issue reported', maintenanceReportedNote: 'Maintenance issue reported — {{note}}',
        lockOpened: 'Door opened', lockClosed: 'Door closed', lockLeftOpen: 'Door left open',
        lockForcedOpen: 'Lock forced open', tamperDetected: 'Tamper detected',
        lockStatusChanged: 'Lock status: {{state}}', lockStatusChangedPlain: 'Lock status changed',
        stayUpdatedGuest: 'Stay updated — {{guest}}', stayUpdated: 'Stay updated',
        roomMovePair: 'Room move: {{from}} → {{to}}', roomMove: 'Room move',
        murAcknowledgedActor: 'Make Up Room acknowledged by {{actor}}', murAcknowledged: 'Make Up Room acknowledged',
        statusChangePair: 'Status: {{from}} → {{to}}', statusChange: 'Status: {{to}}', statusChangePlain: 'Status changed',
        hvacChangeDetail: 'HVAC {{param}} → {{value}}', hvacChangeValue: 'HVAC changed to {{value}}', hvacChange: 'HVAC changed',
        sceneActivatedNamed: 'Scene "{{scene}}" activated', sceneActivated: 'Scene activated',
        alarmRaisedTyped: 'Alarm: {{type}}', alarmRaised: 'Alarm',
        note: 'Note added',
        roomFinishReady: 'Room set-up checked · ready', roomFinishGaps: 'Room set-up checked · gaps found',
    },
    graphs: {
        history: 'History',
        noSamples: 'No temperature samples are available for zone {{zone}}.', zone: 'HVAC zone {{zone}}',
        actual: 'Actual', setpoint: 'Setpoint', chartAria: 'Zone {{zone}} actual temperature versus setpoint',
        sampleNote: 'Samples every {{minutes}} minutes. Missing samples create gaps; source timestamps are preserved by the API.',
        occupancyTimeline: 'Occupancy timeline', occupancyAria: 'Room occupancy timeline',
        loading: 'Loading history…', loadError: 'Room history could not be loaded.',
        noHistory: 'No temperature history is available yet. History begins with the first deployed sample.',
        range: {
            hour: 'Last hour', day: 'Last 24 hours', threeDays: 'Last 3 days', stay: 'Since check-in',
            hourShort: '1h', dayShort: '24h', threeDaysShort: '3d', stayShort: 'Stay',
            groupAria: 'Select time range', stayUnavailable: 'Available only during an active stay.',
        },
        rangeSummary: '{{label}} · {{from}} – {{to}}',
        clampedToRetention: 'Showing the last 90 days; earlier history is not retained.',
        noSamplesInRange: 'No temperature samples were recorded in this range.',
        decimatedNote: 'The chart is simplified for display; peaks and dips are preserved.',
        readoutActual: 'Actual', readoutSetpoint: 'Setpoint',
    },
    // The stay-first room dialog (audit P2): stay card, Stay tab, confirmations.
    stay: {
        vacant: 'No guest',
        nights_one: '{{count}} night', nights_other: '{{count}} nights',
        keysActive_one: '{{count}} key active', keysActive_other: '{{count}} keys active',
        issueAnotherKey: 'Issue another key', noKeys: 'No active keys', replace: 'Replace', revoke: 'Revoke…',
        revokeTitle: 'Revoke key {{uid}}?',
        revokeBody: 'The key stops opening the room at once. Say why, so the log can tell a lost key from a retired one.',
        revokeReason: 'Reason', revokeConfirm: 'Revoke key',
        reason: { lost: 'Lost — reported by the guest', damaged: 'Damaged', other: 'Other' },
        revokeAll: 'Revoke all…', revokeAllTitle: 'Revoke every key for room {{room}}?',
        revokeAllBody_one: '{{count}} key stops opening the room at once.',
        revokeAllBody_other: '{{count}} keys stop opening the room at once.',
        revokeAllConfirm_one: 'Revoke {{count}} key', revokeAllConfirm_other: 'Revoke {{count}} keys',
        dnd: 'Do not disturb', housekeeping: 'Housekeeping',
        // The two state cards under the stay row (D4).
        dndOn: 'On', dndOff: 'Off', clearing: 'Clearing…',
        since: 'since {{time}}', sinceDay: 'since {{day}} {{time}}', cleaningBlocked: 'cleaning blocked',
        makeUpRequested: 'Make-up requested', roomDirty: 'room dirty',
        cleanedAt: 'cleaned {{day}} {{time}}', inspectedAt: 'inspected {{day}} {{time}}',
        dayToday: 'today', dayYesterday: 'yesterday',
        // The day strip (E6): cell words, then the sentence a screen reader gets.
        cellIn: 'in {{time}}', cellOut: 'out {{time}}', cellOutDate: 'out {{date}}', cellToday: 'today',
        cellFrom: '◂ {{date}}', cellUntil: '{{date}} ▸',
        stripArrived: 'Arrived {{date}}', stripLeaves: 'leaves {{date}}', stripVacant: 'No guest. Today is {{date}}',
        nightsLeftOfTotal_one: '{{count}} night left of {{total}}', nightsLeftOfTotal_other: '{{count}} nights left of {{total}}',
        // The Stay tab's facts list and the header's keys pill (D2).
        factsTitle: 'Stay', factArrived: 'Arrived', factLeaves: 'Leaves', factKeys: 'Keys',
        checkedInBy: 'checked in by {{actor}}', overdueSince: 'overdue since {{time}}',
        keysActiveFact_one: '{{count}} active', keysActiveFact_other: '{{count}} active',
        keysPill_one: '{{count}} key', keysPill_other: '{{count}} keys', keysPillTitle: 'Open Door & keys',
        askHousekeeping: 'Ask housekeeping…', askHousekeepingTitle: 'Ask housekeeping about room {{room}}',
        askHousekeepingText: 'Guest asks for the room to be made up.',
        notesForStay: 'Notes for this stay', notes: 'Notes', noNotes: 'No notes yet', addNote: 'Add note',
        addNoteTitle: 'Note for room {{room}}', note: 'Note', saveNote: 'Save note',
        noteHint: 'The desk and housekeeping see it in the room log.',
        checkOutTitle: 'Check out {{guest}}?', checkOutTitleRoom: 'Check out room {{room}}?',
        checkOutBody: 'Room {{room}} · {{nights}} · {{keys}}.', checkOutConfirm: 'Check out',
        keysWillBeRevoked_one: '{{count}} key will be revoked', keysWillBeRevoked_other: '{{count}} keys will be revoked',
        noKeysToRevoke: 'no keys to revoke',
        outOfOrderTitle: 'Mark room {{room}} out of order?',
        outOfOrderBody: 'The room leaves the sellable inventory until maintenance is cleared. A guest in the room stays checked in.',
        outOfServiceTitle: 'Take room {{room}} out of service?',
        outOfServiceBody: 'The room leaves the sellable inventory until it is restored to service. A guest in the room stays checked in.',
        clearMaintenanceTitle: 'Clear maintenance on room {{room}}?',
        restoreTitle: 'Restore room {{room}} to service?',
        backInServiceBody: 'The room returns to its occupancy and housekeeping status and can be sold again once it is ready.',
        // The Stay tab of a vacant room (F6) and the occupied room's Comfort line, keys and door (F8–F11).
        readyToSell: 'Ready to sell', noOpenAlarm: 'no open alarm',
        notSellable: {
            dirty: 'Not ready · dirty', cleaning: 'Not ready · cleaning', to_inspect: 'Not ready · to inspect',
            out_of_order: 'Not ready · out of order', out_of_service: 'Not ready · out of service',
            inactive: 'Not ready · inactive', alarm: 'Not ready · alarm open',
        },
        lastStayTitle: 'Last stay', factGuest: 'Guest', factLeft: 'Left',
        comfort: 'Comfort', comfortTarget: 'target', fullControlsUnderClimate: 'Full controls under Climate',
        addKey: 'Add key', addKeyNeedsCheckout: "Set the stay's checkout first: a key is valid until it.",
        checkInFirstForKeys: 'Check in first to issue keys',
        factDoor: 'Door', doorLastOpened: 'last opened {{when}}', doorByKey: 'key {{n}}', doorByCard: 'card ****{{card}}',
    },
    // The room dialog's live strip (UX-8).
    live: {
        live: 'Live', notResponding: 'Not responding', reconnecting: 'Reconnecting…',
        // Hover titles of the freshness word: the age and the gateways go here, never in the text.
        updatedSeconds: 'updated {{count}} s ago', updatedMinutes: 'updated {{count}} min ago',
        updatedAt: 'updated at {{time}}', locked: 'Locked', unlocked: 'Unlocked', setpoint: 'set {{value}}',
        lights_one: '{{count}} light on', lights_other: '{{count}} lights on',
        silentTitle: 'Readings from {{time}} · {{gateway}} not reporting',
        silentTitleNoTime: 'Last known readings · {{gateway}} not reporting',
        sessionDown: 'Live updates paused · last known values',
    },
};
export const enHousekeepingTranslations = {
    title: 'Housekeeping', myRooms: 'My Rooms', assignedReady: '{{assigned}} assigned · {{ready}} ready',
    murFirst: 'MUR first', noAssigned: 'No assigned rooms need attention',
    noAssignedDescription: 'Your assigned rooms will appear here.', search: 'Search rooms...',
    allAttendants: 'All attendants', dirty: 'Dirty', cleaning: 'Cleaning', toInspect: 'To Inspect', ready: 'Ready',
    workload: '{{assigned}} assigned · {{completed}} completed today', selected: '{{count}} selected',
    clearSelection: 'Clear selection', filterByAttendant: 'Attendant',
    startCleaning: 'Start Cleaning', markDone: 'Mark Done', inspect: 'Mark inspected', markForService: 'Mark for service', assignTo: 'Assign to...', assign: 'Assign',
    noAttention: 'No rooms need attention', allClean: 'All rooms are ready', allRooms: 'All Rooms',
    guestRoom: 'Guest room', dndBlocked: 'Do not disturb is active. Cleaning cannot start.',
    reportIssue: 'Report issue', reportIssueTitle: 'Report issue for room {{room}}',
    reportIssueDescription: 'This creates a maintenance event and marks the room out of order for follow-up.',
    issuePlaceholder: 'Describe the issue (optional)', suggestedStatus: 'Suggested status: Out of Order',
    reporting: 'Reporting…', inspectionNote: 'Inspection note', markDirty: 'Mark Dirty', fail: 'Fail',
    awaitingInspection: 'Awaiting inspection', done: '✓ Done', cannotCleanDnd: 'Cannot clean — DND active',
    dndCount: 'on DND', statusFilterAria: 'Housekeeping status',
    waiting: { dirty: 'dirty for {{age}}', cleaning: 'cleaning for {{age}}', to_inspect: 'waiting for inspection {{age}}' },
    age: { underMinute: 'under a minute' },
    deskNote: 'Desk note', deskNoteBy: '{{actor}}, {{time}}',
    filteredTitle: 'No room matches these filters',
    filteredDescription: 'Rooms are on housekeeping, but the search, floor, status or attendant filter hides every one.',
    noRoomsTitle: 'No rooms on housekeeping', noRoomsDescription: 'Rooms appear here once they carry a housekeeping status.',
    stay: {
        updateError: 'The stay could not be updated', missingChipType: 'The encoded card chip type is missing',
        cardSaveError: 'The stay was checked in, but the encoded guest card was not saved',
        checkInTitle: 'Check In', editTitle: 'Edit Stay', guestName: 'Guest Name', guestPlaceholder: 'Guest name (optional)',
        expectedCheckout: 'Expected Checkout (local time)', autoCard: 'A guest card will be encoded and issued as the next check-in step.',
        moveToRoom: 'Move to room', keepRoom: 'Keep current room', moveDescription: 'The old room becomes dirty. Active guest cards follow the new room.',
        rewrite_one: '{{count}} active card must be re-encoded for offline lock access.',
        rewrite_other: '{{count}} active cards must be re-encoded for offline lock access.',
        moving: 'Moving stay...', saving: 'Saving...', continueCard: 'Continue to Card', saveMove: 'Save & Move', saveChanges: 'Save Changes',
    },
};
export const hrGrmsTranslations = {
    status: {
        total: 'Ukupno', occupied: 'Zauzeto', ready: 'Spremno', dirty: 'Prljavo',
        cleaning: 'Čišćenje', to_inspect: 'Za pregled', out_of_order: 'U kvaru',
        out_of_service: 'Izvan uporabe', needs_housekeeping: 'Potrebno čišćenje',
        unavailable: 'Nedostupno', notOccupied: 'Nije zauzeto',
        vacant: 'Slobodno', in_service: 'U uporabi',
    },
    // Sažeti znakovi na karticama i ćelijama tlocrta. Puni nazivi iz `status`
    // ostaju za filtre, zaglavlje ladice, naslove i pristupačna imena.
    statusShort: {
        occupied: 'Zauzeto', ready: 'Spremno', dirty: 'Prljavo', cleaning: 'Čišćenje',
        to_inspect: 'Za pregled', out_of_order: 'U kvaru', out_of_service: 'Izvan uporabe',
        needs_housekeeping: 'Za čišćenje',
    },
    common: { room: 'Soba', guest: 'Gost', all: 'Sve', status: 'Status', access: 'Pristup', alarms: 'Alarmi', log: 'Dnevnik', graphs: 'Grafovi', retry: 'Pokušaj ponovno', cancel: 'Odustani', done: 'Gotovo', on: 'Uključeno', off: 'Isključeno', open: 'Otvoreno', closed: 'Zatvoreno', unknown: 'nepoznato' },
    dashboard: {
        search: 'Pretraži...', searchRoomsGuests: 'Pretraži sobe i goste...', filters: 'Filtri', clear: 'Očisti',
        dashboardView: 'Prikaz nadzorne ploče', showRoomGrid: 'Prikaži mrežu soba', roomGrid: 'Mreža soba',
        showFloorPlans: 'Prikaži tlocrte', floorPlans: 'Tlocrti', gridView: 'Mrežni prikaz', compactView: 'Sažeti prikaz',
        unassigned: 'Nedodijeljeno', noRooms: 'Još nema soba',
        noRoomsDescription: 'Sobe će se pojaviti nakon konfiguriranja u sustavu.', noMatches: 'Nema odgovarajućih soba',
        noMatchesDescription: 'Pokušajte prilagoditi filtre.', clearFilters: 'Očisti filtre',
        roomAria: 'Soba {{room}}, {{status}}', windowOpen: 'Prozor je otvoren',
        doorOpen: 'Vrata otvorena', doorClosed: 'Vrata zatvorena', modeUnknown: 'Način nepoznat',
        lastReported: 'Zadnje javljeno', lastReadingGatewayOffline: 'Ne javlja se · zadnje očitanje {{time}}',
        gatewayOffline: 'Ne javlja se', gatewayNotReporting: '{{gateway}} se ne javlja', lockLocked: 'Brava zaključana',
        lockUnlocked: 'Barem jedna brava javlja otključano stanje', lockUnknown: 'Stanje brave nepoznato',
        legendColor: 'Boja',
        floorOrderUnavailable: 'Redoslijed katova nedostupan — katovi se prikazuju u nepotvrđenom redoslijedu.',
    },
    filters: {
        alarmsOnly: 'Samo alarmi', floor: 'Kat', allFloors: 'Svi katovi', type: 'Vrsta',
        dnd: 'Ne ometaj', mur: 'Pospremanje sobe',
    },
    alarms: {
        critical_one: 'Kritični alarm', critical_few: '{{count}} kritična alarma', critical_other: '{{count}} kritičnih alarma',
        fallback: 'Kritični alarm', more: '(još {{count}})', dismiss: 'Zatvori traku alarma', criticalLabel: 'Kritično',
        warnings: 'Upozorenja', warning: 'Upozorenje', alarms: 'Alarmi', info: 'Info', mute: 'Isključi zvuk alarma',
        unmute: 'Uključi zvuk alarma', acknowledge: 'Potvrdi', acknowledged: 'Potvrđeno', resolve: 'Riješi',
        createdAt: 'Stvoreno', acknowledgedAt: 'Potvrđeno', sos: 'SOS',
        motionInVacant: 'Otkriven pokret u praznoj sobi',
        hvacSnoozed: 'Potvrdio {{who}} · ponovna provjera {{time}}',
        inRoom: '{{alarm}} u sobi {{room}}',
    },
    flags: { dnd: 'Ne ometaj', mur: 'Pospremanje' },
    floorPlan: {
        positioned: '{{count}} postavljeno', openRoom: 'Otvori sobu {{room}}, {{status}}',
        noMatchingPlacements: 'Nijedna odgovarajuća soba nema upotrebljiv položaj na ovom tlocrtu.',
        unplacedSection: 'Sobe bez položaja na tlocrtu', unplacedTitle: 'Sobe bez upotrebljivog položaja',
        unplacedDescription: 'Te su sobe i dalje dostupne u mrežnom prikazu.',
    },
    desk: {
        today: 'Danas', checkInGuest: 'Prijavi gosta',
        tile: {
            arrivals: 'Dolasci', departures: 'Odlasci', sellable: 'Spremno za prodaju', attention: 'Traži pažnju',
        },
        tileSub: {
            arrivals: 'prijavljeno danas', departures: 'preostalo · odjava {{time}}',
            departuresOut: 'odjavljeno {{count}} ·', departuresOverdue: 'preostalo · {{count}} kasni',
        },
        showOnBoard: 'Prikaži na ploči: {{tile}}', clearTile: 'Prestani prikazivati samo: {{tile}}',
        attention: 'Pažnja', openActivity: 'Otvori aktivnosti',
        countAria_one: '{{count}} stavka traži pažnju', countAria_few: '{{count}} stavke traže pažnju',
        countAria_other: '{{count}} stavki traži pažnju',
        action: {
            acknowledge: 'Potvrdi', openStay: 'Otvori boravak', openRoom: 'Otvori sobu', openClimate: 'Otvori klimu',
            notifyHousekeeping: 'Obavijesti domaćinstvo', details: 'Detalji', hideDetails: 'Sakrij detalje',
            open: 'Otvori', notify: 'Obavijesti', openKeys: 'Otvori ključeve', checkOut: 'Odjavi',
        },
        row: {
            overdue: 'Odjava kasni', overdueDetail: '{{guest}} · odjava {{time}}', departure: 'Odlazi danas',
            keysActive_one: '{{count}} aktivan ključ', keysActive_few: '{{count}} aktivna ključa',
            keysActive_other: '{{count}} aktivnih ključeva',
            keys_one: '{{count}} ključ', keys_few: '{{count}} ključa', keys_other: '{{count}} ključeva',
            doorLeftOpen: 'Vrata ostavljena otvorena', doorVacant: 'Prazna soba', doorDnd: 'Ne uznemiravaj',
            mur: 'Zatraženo pospremanje sobe', gateway_one: 'Soba {{rooms}} se ne javlja', gateway_few: 'Sobe {{rooms}} se ne javljaju', gateway_other: 'Sobe {{rooms}} se ne javljaju',
            gatewayShown: 'prikazana zadnja očitanja', gatewaySince: 'od {{time}}', gatewayTitle: 'Pristupnik {{gateway}}',
            critical: 'Kritični alarm', warning: 'Upozorenje', acknowledgedBy: 'potvrdio {{user}}',
            inHouse: '{{guest}} u hotelu', guest: 'Gost',
            keysExpiring_one: '{{count}} ključ istječe danas', keysExpiring_few: '{{count}} ključa istječu danas',
            keysExpiring_other: '{{count}} ključeva istječe danas',
            keysUntil: 'vrijedi do {{time}}', longDirty: 'Prljava dulje od dana',
        },
        nightAudit: {
            title: 'Noćna revizija', toggle: 'Noćna revizija',
            hint: 'Prikaži samo zakašnjele odjave, ključeve koji istječu danas i sobe prljave dulje od dana',
            nothing: 'Ništa za zaključiti: nema zakašnjelih odjava, ključeva koji istječu danas ni soba prljavih dulje od dana.',
        },
        alarm: {
            sos: 'Pritisnut SOS', lock_forced: 'Vrata nasilno otvorena', tamper: 'Alarm neovlaštenog zahvata',
            burglary: 'Provalni alarm', hvac_deviation: 'Temperatura odstupa od zadane',
            probe_error: 'Kvar temperaturne sonde', motion_in_vacant: 'Pokret u praznoj sobi',
            overdue_checkout: 'Odjava kasni',
        },
        age: {
            seconds: 'prije {{count}} s', minutes: '{{count}} min', hours: '{{hours}} h {{minutes}}',
            over: 'kasni {{age}}',
        },
        pane: { label: 'Prikaz recepcije', desk: 'Recepcija', board: 'Ploča', activity: 'Aktivnost' },
        ready: 'Spremna', notReady: 'Nije spremna', noSensors: 'Nema senzora',
        notReadyWhy: {
            commissioning: 'Nije spremna · u uspostavi', maintenance: 'Nije spremna · održavanje', inactive: 'Nije spremna · neaktivna',
            alarm: 'Nije spremna · otvoren alarm',
        },
        toolbar: {
            rooms: 'Sobe', floorPlan: 'Tlocrt', compact: 'Sažeto', alarmSound: 'Zvuk alarma', more: 'Više mogućnosti ploče',
        },
    },
    drawer: {
        details: 'Detalji sobe {{room}}', description: 'Boravak, klima, vrata i ključevi, dnevnik',
        hvac: 'HVAC', access: 'Pristup', log: 'Dnevnik', graphs: 'Grafovi',
        stayTab: 'Boravak', climateTab: 'Klima', doorKeysTab: 'Vrata i ključevi', more: 'Više',
        checkInAt: 'Prijava: {{date}}', expectedCheckout: 'Očekivana odjava: {{date}}', checkIn: 'Prijavi gosta',
        editStay: 'Uredi boravak', checkOut: 'Odjavi gosta', markOutOfOrder: 'Označi kao u kvaru',
        markOutOfService: 'Označi izvan uporabe', startCleaning: 'Započni čišćenje',
        markDone: 'Označi kao dovršeno', markInspected: 'Označi kao pregledano', markForService: 'Označi za servis',
        clearMaintenance: 'Ukloni održavanje', restoreToService: 'Vrati u uporabu',
        presence: 'U sobi', noPresence: 'Nema prisutnosti', lightsOn: '{{count}} uključeno', noDeviceData: 'Nema podataka s uređaja',
    },
    feedback: {
        checkOut: { done: 'Gost je odjavljen', failed: 'Odjava nije uspjela' },
        housekeeping: { done: 'Status čišćenja ažuriran', failed: 'Ažuriranje čišćenja nije uspjelo' },
        serviceState: { done: 'Stanje sobe ažurirano', failed: 'Promjena stanja sobe nije uspjela' },
        alarmAcknowledge: { done: 'Alarm potvrđen', failed: 'Alarm nije moguće potvrditi' },
        alarmResolve: { done: 'Alarm riješen', failed: 'Alarm nije moguće riješiti' },
        murAcknowledge: { done: 'Pospremanje sobe potvrđeno', failed: 'Pospremanje sobe nije moguće potvrditi' },
        cardRevoke: { done: 'Kartica opozvana', failed: 'Karticu nije moguće opozvati' },
        cardReportLost: { done: 'Ključ prijavljen kao izgubljen i opozvan', failed: 'Ključ nije moguće prijaviti kao izgubljen' },
        note: { done: 'Bilješka spremljena', failed: 'Bilješka nije spremljena' },
        cardsRevokeAll: { done: 'Sve kartice sobe opozvane', failed: 'Kartice sobe nije moguće opozvati' },
        lockLock: { done: 'Naredba za zaključavanje poslana', failed: 'Vrata nije moguće zaključati' },
        lockUnlock: { done: 'Naredba za otključavanje poslana', failed: 'Vrata nije moguće otključati' },
        lockOpen: { done: 'Naredba za otvaranje poslana', failed: 'Vrata nije moguće otvoriti' },
        climate: { done: 'Postavka klime spremljena', failed: 'Postavka klime nije spremljena' },
        scene: { done: 'Scena aktivirana', failed: 'Scenu nije moguće aktivirati' },
    },
    access: {
        opened: 'Otvoreno', closed: 'Zatvoreno', noLocksCards: 'Za ovu sobu nisu konfigurirane brave ni kartice',
        insufficientPermissions: 'Nedovoljne ovlasti',
        readerNotReady: 'Čitač kartica nije spreman', guestCards: 'Kartice gostiju ({{count}})', issueCard: 'Izdaj karticu',
        revokeAll: 'Opozovi sve', placeCard: 'Čitač je spreman · postavite praznu karticu za izdavanje', cardReady: 'Kartica spremna: {{uid}}',
        guest: 'Gost', validUntil: 'do {{date}}', revokeCard: 'Opozovi karticu', replaceCard: 'Zamijeni karticu',
        critical: 'Kritično', lock: 'Zaključaj', unlock: 'Otključaj', open: 'Otvori', active: 'Aktivna', pending: 'Na čekanju',
        suspended: 'Obustavljena', revoked: 'Opozvana', expired: 'Istekla', lost: 'Izgubljena',
        lastAccess: 'Zadnji pristup',
        lockbell: 'Zvono na vratima', minibar: 'Minibar',
        cardSuffix: 'Kartica ****{{card}}',
    },
    hvac: {
        none: 'Nisu konfigurirane HVAC zone ni temperaturni senzori', ecoMode: 'Eko način', quickScenes: 'Brze scene',
        lowerSetpoint: 'Snizi zadanu vrijednost', raiseSetpoint: 'Podigni zadanu vrijednost',
        modeSetForBuilding: 'Postavlja se za cijelu zgradu', windowDisabled: 'Prozor je otvoren — HVAC je onemogućen',
        mode: 'Način rada', fan: 'Ventilator', actual: 'Stvarno', vacantRoomWarning: 'Soba je prazna; automatizacija zauzetosti može zamijeniti ove postavke pri prijavi.',
        option: { auto: 'Automatski', off: 'Isključeno', cool: 'Hlađenje', heat: 'Grijanje', fan_only: 'Ventilator', dry: 'Odvlaživanje', low: 'Nisko', med: 'Srednje', medium: 'Srednje', high: 'Visoko', turbo: 'Turbo' },
        setpointOutOfRange: 'Soba javlja {{value}}°C, izvan konfiguriranog raspona {{min}}–{{max}}°C. Prilagodite na uređaju ili proširite raspon u parametrima sobe.',
        special: { standby: 'Pripravnost', protection: 'Zaštita' },
        setback: { standby: 'Ušteda dok soba nije zauzeta', protection: 'Zaštita od smrzavanja i pregrijavanja' },
        hotel: {
            base: 'Osnova hotela',
            pending: 'Primjena u tijeku…', pendingValue: 'Na čekanju {{value}}°C',
            rejected: 'Uređaj je odbio zadnju promjenu', conflict: 'Uređaj ima drugačiju politiku',
            retry: 'Ponovno pošalji',
            acknowledge: 'Koristi vrijednosti uređaja',
            locked: 'Kontrole gosta su zaključane za ovu zonu',
        },
    },
    log: {
        all: 'Sve', access: 'Pristup', status: 'Status', housekeeping: 'Domaćinstvo', maintenance: 'Održavanje',
        hvac: 'Klima', scenes: 'Scene', alarms: 'Alarmi',
        manualOnly: 'Samo ručno', allAccess: 'Svi pristupi', today: 'Danas', yesterday: 'Jučer',
        showDeviceEvents: 'Prikaži događaje uređaja',
        none: 'Nema zabilježenih događaja', loadMore: 'Učitaj još', inspectionNote: 'Napomena pregleda: {{note}}',
        unknownReason: 'Nepoznat razlog', accessGranted: 'Pristup odobren', accessGrantedActor: 'Pristup odobren — {{actor}}',
        accessGrantedCard: 'Pristup odobren karticom ...{{card}}', accessGrantedActorCard: 'Pristup odobren — {{actor}} (kartica završava s ...{{card}})',
        accessDenied: 'Pristup odbijen — {{reason}}', manualUnlock: 'Ručno otključavanje', manualUnlockActor: 'Ručno otključao/la {{actor}}',
        manualLock: 'Ručno zaključavanje', manualLockActor: 'Ručno zaključao/la {{actor}}', emergencyUnlock: 'Otključavanje u nuždi',
        emergencyUnlockActor: 'Otključavanje u nuždi — {{actor}}', checkIn: 'Prijava — {{guest}}', checkOut: 'Odjava — {{guest}}',
        dndOn: 'Ne ometaj uključeno', dndOff: 'Ne ometaj isključeno', murOn: 'Pospremanje sobe uključeno', murOff: 'Pospremanje sobe isključeno',
        cleaningStarted: 'Čišćenje započeto', cleaningStartedActor: 'Čišćenje započeo/la {{actor}}',
        cleaningDone: 'Čišćenje dovršeno', cleaningDoneActor: 'Čišćenje dovršio/la {{actor}}', inspected: 'Soba pregledana',
        inspectedActor: 'Sobu pregledao/la {{actor}}', maintenanceReported: 'Prijavljen problem održavanja',
        maintenanceReportedNote: 'Prijavljen problem održavanja — {{note}}',
        lockOpened: 'Vrata otvorena', lockClosed: 'Vrata zatvorena', lockLeftOpen: 'Vrata ostavljena otvorena',
        lockForcedOpen: 'Brava nasilno otvorena', tamperDetected: 'Otkriveno neovlašteno diranje',
        lockStatusChanged: 'Status brave: {{state}}', lockStatusChangedPlain: 'Status brave promijenjen',
        stayUpdatedGuest: 'Boravak ažuriran — {{guest}}', stayUpdated: 'Boravak ažuriran',
        roomMovePair: 'Premještaj sobe: {{from}} → {{to}}', roomMove: 'Premještaj sobe',
        murAcknowledgedActor: 'Pospremanje sobe potvrdio/la {{actor}}', murAcknowledged: 'Pospremanje sobe potvrđeno',
        statusChangePair: 'Status: {{from}} → {{to}}', statusChange: 'Status: {{to}}', statusChangePlain: 'Status promijenjen',
        hvacChangeDetail: 'HVAC {{param}} → {{value}}', hvacChangeValue: 'HVAC promijenjen na {{value}}', hvacChange: 'HVAC promijenjen',
        sceneActivatedNamed: 'Scena „{{scene}}” aktivirana', sceneActivated: 'Scena aktivirana',
        alarmRaisedTyped: 'Alarm: {{type}}', alarmRaised: 'Alarm',
        note: 'Bilješka dodana',
        roomFinishReady: 'Postavke sobe provjerene · spremna', roomFinishGaps: 'Postavke sobe provjerene · ima nedostataka',
    },
    graphs: {
        history: 'Povijest',
        noSamples: 'Nema dostupnih temperaturnih uzoraka za zonu {{zone}}.', zone: 'HVAC zona {{zone}}', actual: 'Stvarno',
        setpoint: 'Zadana vrijednost', chartAria: 'Stvarna temperatura zone {{zone}} u odnosu na zadanu vrijednost',
        sampleNote: 'Uzorci svakih {{minutes}} minuta. Nedostajući uzorci stvaraju praznine; API čuva izvorne vremenske oznake.',
        occupancyTimeline: 'Vremenska crta zauzetosti', occupancyAria: 'Vremenska crta zauzetosti sobe',
        loading: 'Učitavanje povijesti…', loadError: 'Nije moguće učitati povijest sobe.',
        noHistory: 'Povijest temperature još nije dostupna. Počinje s prvim dostavljenim uzorkom.',
        range: {
            hour: 'Posljednji sat', day: 'Posljednja 24 sata', threeDays: 'Posljednja 3 dana', stay: 'Od prijave',
            hourShort: '1h', dayShort: '24h', threeDaysShort: '3d', stayShort: 'Boravak',
            groupAria: 'Odabir vremenskog raspona', stayUnavailable: 'Dostupno samo tijekom aktivnog boravka.',
        },
        rangeSummary: '{{label}} · {{from}} – {{to}}',
        clampedToRetention: 'Prikazuje se posljednjih 90 dana; starija se povijest ne čuva.',
        noSamplesInRange: 'U ovom rasponu nema zabilježenih temperaturnih uzoraka.',
        decimatedNote: 'Graf je pojednostavljen za prikaz; vrhovi i padovi ostaju sačuvani.',
        readoutActual: 'Stvarno', readoutSetpoint: 'Zadano',
    },
    stay: {
        vacant: 'Nema gosta',
        nights_one: '{{count}} noć', nights_few: '{{count}} noći', nights_other: '{{count}} noći',
        keysActive_one: '{{count}} aktivan ključ', keysActive_few: '{{count}} aktivna ključa',
        keysActive_other: '{{count}} aktivnih ključeva',
        issueAnotherKey: 'Izdaj još jedan ključ', noKeys: 'Nema aktivnih ključeva', replace: 'Zamijeni', revoke: 'Opozovi…',
        revokeTitle: 'Opozvati ključ {{uid}}?',
        revokeBody: 'Ključ odmah prestaje otvarati sobu. Navedite razlog kako bi dnevnik razlikovao izgubljeni ključ od povučenog.',
        revokeReason: 'Razlog', revokeConfirm: 'Opozovi ključ',
        reason: { lost: 'Izgubljen — prijavio gost', damaged: 'Oštećen', other: 'Drugo' },
        revokeAll: 'Opozovi sve…', revokeAllTitle: 'Opozvati sve ključeve sobe {{room}}?',
        revokeAllBody_one: '{{count}} ključ odmah prestaje otvarati sobu.',
        revokeAllBody_few: '{{count}} ključa odmah prestaju otvarati sobu.',
        revokeAllBody_other: '{{count}} ključeva odmah prestaje otvarati sobu.',
        revokeAllConfirm_one: 'Opozovi {{count}} ključ', revokeAllConfirm_few: 'Opozovi {{count}} ključa',
        revokeAllConfirm_other: 'Opozovi {{count}} ključeva',
        dnd: 'Ne ometaj', housekeeping: 'Domaćinstvo',
        dndOn: 'Uključeno', dndOff: 'Isključeno', clearing: 'Poništavanje…',
        since: 'od {{time}}', sinceDay: 'od {{day}} {{time}}', cleaningBlocked: 'čišćenje blokirano',
        makeUpRequested: 'Zatraženo pospremanje', roomDirty: 'soba je prljava',
        cleanedAt: 'očišćeno {{day}} {{time}}', inspectedAt: 'pregledano {{day}} {{time}}',
        dayToday: 'danas', dayYesterday: 'jučer',
        cellIn: 'dol. {{time}}', cellOut: 'odl. {{time}}', cellOutDate: 'odl. {{date}}', cellToday: 'danas',
        cellFrom: '◂ {{date}}', cellUntil: '{{date}} ▸',
        stripArrived: 'Dolazak {{date}}', stripLeaves: 'odlazak {{date}}', stripVacant: 'Nema gosta. Danas je {{date}}',
        nightsLeftOfTotal_one: 'još {{count}} noć od {{total}}', nightsLeftOfTotal_few: 'još {{count}} noći od {{total}}',
        nightsLeftOfTotal_other: 'još {{count}} noći od {{total}}',
        factsTitle: 'Boravak', factArrived: 'Dolazak', factLeaves: 'Odlazak', factKeys: 'Ključevi',
        checkedInBy: 'prijavio {{actor}}', overdueSince: 'kasni od {{time}}',
        keysActiveFact_one: '{{count}} aktivan', keysActiveFact_few: '{{count}} aktivna', keysActiveFact_other: '{{count}} aktivnih',
        keysPill_one: '{{count}} ključ', keysPill_few: '{{count}} ključa', keysPill_other: '{{count}} ključeva',
        keysPillTitle: 'Otvori Vrata i ključeve',
        askHousekeeping: 'Javi domaćinstvu…', askHousekeepingTitle: 'Poruka domaćinstvu za sobu {{room}}',
        askHousekeepingText: 'Gost traži pospremanje sobe.',
        notesForStay: 'Bilješke za ovaj boravak', notes: 'Bilješke', noNotes: 'Još nema bilješki', addNote: 'Dodaj bilješku',
        addNoteTitle: 'Bilješka za sobu {{room}}', note: 'Bilješka', saveNote: 'Spremi bilješku',
        noteHint: 'Recepcija i domaćinstvo vide je u dnevniku sobe.',
        checkOutTitle: 'Odjaviti gosta {{guest}}?', checkOutTitleRoom: 'Odjaviti sobu {{room}}?',
        checkOutBody: 'Soba {{room}} · {{nights}} · {{keys}}.', checkOutConfirm: 'Odjavi',
        keysWillBeRevoked_one: '{{count}} ključ bit će opozvan', keysWillBeRevoked_few: '{{count}} ključa bit će opozvana',
        keysWillBeRevoked_other: '{{count}} ključeva bit će opozvano',
        noKeysToRevoke: 'nema ključeva za opoziv',
        outOfOrderTitle: 'Označiti sobu {{room}} kao u kvaru?',
        outOfOrderBody: 'Soba izlazi iz prodajnog kapaciteta dok se održavanje ne ukloni. Gost u sobi ostaje prijavljen.',
        outOfServiceTitle: 'Staviti sobu {{room}} izvan uporabe?',
        outOfServiceBody: 'Soba izlazi iz prodajnog kapaciteta dok se ne vrati u uporabu. Gost u sobi ostaje prijavljen.',
        clearMaintenanceTitle: 'Ukloniti održavanje sobe {{room}}?',
        restoreTitle: 'Vratiti sobu {{room}} u uporabu?',
        backInServiceBody: 'Soba se vraća na svoje stanje zauzetosti i čišćenja i može se ponovno prodati kad bude spremna.',
        readyToSell: 'Spremna za prodaju', noOpenAlarm: 'nema otvorenog alarma',
        notSellable: {
            dirty: 'Nije spremna · prljava', cleaning: 'Nije spremna · čisti se', to_inspect: 'Nije spremna · za pregled',
            out_of_order: 'Nije spremna · u kvaru', out_of_service: 'Nije spremna · izvan uporabe',
            inactive: 'Nije spremna · neaktivna', alarm: 'Nije spremna · otvoren alarm',
        },
        lastStayTitle: 'Zadnji boravak', factGuest: 'Gost', factLeft: 'Odjava',
        comfort: 'Udobnost', comfortTarget: 'cilj', fullControlsUnderClimate: 'Sve postavke na kartici Klima',
        addKey: 'Dodaj ključ', addKeyNeedsCheckout: 'Najprije postavite odjavu boravka: ključ vrijedi do nje.',
        checkInFirstForKeys: 'Najprije prijavite gosta da biste izdali ključeve',
        factDoor: 'Vrata', doorLastOpened: 'zadnji put otvorena {{when}}', doorByKey: 'ključ {{n}}', doorByCard: 'kartica ****{{card}}',
    },
    live: {
        live: 'Uživo', notResponding: 'Ne javlja se', reconnecting: 'Ponovno povezivanje…',
        updatedSeconds: 'ažurirano prije {{count}} s', updatedMinutes: 'ažurirano prije {{count}} min',
        updatedAt: 'ažurirano u {{time}}', locked: 'Zaključano', unlocked: 'Otključano', setpoint: 'zadano {{value}}',
        lights_one: '{{count}} svjetlo uključeno', lights_few: '{{count}} svjetla uključena',
        lights_other: '{{count}} svjetala uključeno',
        silentTitle: 'Očitanja od {{time}} · {{gateway}} se ne javlja',
        silentTitleNoTime: 'Zadnja poznata očitanja · {{gateway}} se ne javlja',
        sessionDown: 'Ažuriranja uživo pauzirana · posljednje poznate vrijednosti',
    },
};
export const hrHousekeepingTranslations = {
    title: 'Domaćinstvo', myRooms: 'Moje sobe', assignedReady: '{{assigned}} dodijeljeno · {{ready}} spremno',
    murFirst: 'Pospremanje prvo', noAssigned: 'Nijedna dodijeljena soba ne zahtijeva pažnju',
    noAssignedDescription: 'Dodijeljene sobe pojavit će se ovdje.', search: 'Pretraži sobe...', allAttendants: 'Svi djelatnici',
    dirty: 'Prljavo', cleaning: 'Čišćenje', toInspect: 'Za pregled', ready: 'Spremno',
    workload: '{{assigned}} dodijeljeno · {{completed}} dovršeno danas', selected: '{{count}} odabrano',
    clearSelection: 'Očisti odabir', filterByAttendant: 'Djelatnik',
    startCleaning: 'Započni čišćenje', markDone: 'Označi kao dovršeno', inspect: 'Označi kao pregledano', markForService: 'Označi za servis', assignTo: 'Dodijeli...', assign: 'Dodijeli',
    noAttention: 'Nijedna soba ne zahtijeva pažnju', allClean: 'Sve su sobe spremne', allRooms: 'Sve sobe',
    guestRoom: 'Gostinjska soba', dndBlocked: 'Ne ometaj je uključeno. Čišćenje se ne može započeti.',
    reportIssue: 'Prijavi problem', reportIssueTitle: 'Prijavi problem za sobu {{room}}',
    reportIssueDescription: 'Stvara se događaj održavanja i soba se označava kao u kvaru radi daljnje obrade.',
    issuePlaceholder: 'Opišite problem (neobavezno)', suggestedStatus: 'Predloženi status: U kvaru',
    reporting: 'Prijava u tijeku…', inspectionNote: 'Napomena pregleda', markDirty: 'Označi kao prljavo', fail: 'Nije prošlo',
    awaitingInspection: 'Čeka pregled', done: '✓ Gotovo', cannotCleanDnd: 'Nije moguće čistiti — uključeno je Ne ometaj',
    dndCount: 's Ne ometaj', statusFilterAria: 'Status čišćenja',
    waiting: { dirty: 'prljava {{age}}', cleaning: 'čisti se {{age}}', to_inspect: 'čeka pregled {{age}}' },
    age: { underMinute: 'manje od minute' },
    deskNote: 'Napomena recepcije', deskNoteBy: '{{actor}}, {{time}}',
    filteredTitle: 'Nijedna soba ne odgovara filtrima',
    filteredDescription: 'Sobe su u domaćinstvu, ali filtar pretrage, kata, statusa ili djelatnika skriva svaku.',
    noRoomsTitle: 'Nema soba u domaćinstvu', noRoomsDescription: 'Sobe se ovdje pojavljuju kada imaju status čišćenja.',
    stay: {
        updateError: 'Boravak nije moguće ažurirati', missingChipType: 'Nedostaje vrsta čipa kodirane kartice',
        cardSaveError: 'Gost je prijavljen, ali kodirana kartica gosta nije spremljena', checkInTitle: 'Prijava gosta',
        editTitle: 'Uredi boravak', guestName: 'Ime gosta', guestPlaceholder: 'Ime gosta (neobavezno)',
        expectedCheckout: 'Očekivana odjava (lokalno vrijeme)', autoCard: 'Kartica gosta kodirat će se i izdati u sljedećem koraku prijave.',
        moveToRoom: 'Premjesti u sobu', keepRoom: 'Zadrži trenutačnu sobu',
        moveDescription: 'Stara soba postaje prljava. Aktivne kartice gosta prenose se u novu sobu.',
        rewrite_one: '{{count}} aktivnu karticu treba ponovno kodirati za izvanmrežni pristup bravi.',
        rewrite_few: '{{count}} aktivne kartice treba ponovno kodirati za izvanmrežni pristup bravi.',
        rewrite_other: '{{count}} aktivnih kartica treba ponovno kodirati za izvanmrežni pristup bravi.',
        moving: 'Premještanje boravka...', saving: 'Spremanje...', continueCard: 'Nastavi na karticu',
        saveMove: 'Spremi i premjesti', saveChanges: 'Spremi promjene',
    },
};
//# sourceMappingURL=grmsTranslations.js.map